import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import Timer from "@/components/Timer";
import CrosswordGrid, { CrosswordGridRef } from "@/components/CrosswordGrid";
import { UiClue, crosswordUiCell } from "@/utils/crosswordUtils";
import { Button } from "./ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import CompletionDialog from "@/components/CompletionDialog";
import { Link } from "react-router-dom";
import MobileKeyboard from "@/components/MobileKeyboard";

interface MobileCrosswordLayoutProps {
  title: string;
  gridData: crosswordUiCell[][];
  onCellChange: (row: number, col: number, value: string) => void;
  activeCell: { row: number; col: number } | null;
  setActiveCell: (cell: { row: number; col: number } | null) => void;
  setActiveClue: (number: number | null) => void;
  direction: 'across' | 'down';
  setDirection: (direction: 'across' | 'down') => void;
  isComplete: boolean;
  isTimerRunning: boolean;
  onReset: () => void;
  onTimerStop: (time: number) => void;
  onCheck?: () => void;
  onReveal?: (type: 'square' | 'word' | 'puzzle') => void;
  onShare?: () => void;
  currentClue: UiClue | null;
  acrossClues: UiClue[];
  downClues: UiClue[];
  activeClueNumber: number | null;
  onClueSelect: (row: number, col: number, direction: 'across' | 'down') => void;
  showCompletionDialog: boolean;
  setShowCompletionDialog: (show: boolean) => void;
  completionTime: number;
  shareUrl: string;
}

const MobileCrosswordLayout: React.FC<MobileCrosswordLayoutProps> = ({
  title,
  gridData,
  onCellChange,
  activeCell,
  setActiveCell,
  setActiveClue,
  direction,
  setDirection,
  isComplete,
  isTimerRunning,
  onReset,
  onTimerStop,
  onCheck,
  onReveal,
  onShare,
  currentClue,
  acrossClues,
  downClues,
  activeClueNumber,
  onClueSelect,
  showCompletionDialog,
  setShowCompletionDialog,
  completionTime,
  shareUrl
}) => {
  const gridRef = useRef<CrosswordGridRef>(null);

  const calculateGridSize = () => {
    if (!gridData.length || !gridData[0].length) return 0;
    
    // Calculate the maximum dimension
    const maxDimension = Math.max(gridData.length, gridData[0].length);
    
    // For mobile, we want the grid to take up around 90% of the screen width
    const viewportWidth = window.innerWidth;
    const maxGridWidth = viewportWidth * 0.99; // 95% of screen width
    
    // Calculate cell size
    return Math.min(32, Math.floor(maxGridWidth / maxDimension));
  };

  const nextClue = () => {
    const clues = direction === 'across' ? acrossClues : downClues;
    if (!activeClueNumber || clues.length === 0) return;
    
    const currentIndex = clues.findIndex(clue => clue.number === activeClueNumber);
    if (currentIndex === -1) return;
    
    const nextIndex = (currentIndex + 1) % clues.length;
    const nextClue = clues[nextIndex];
    
    onClueSelect(nextClue.row, nextClue.col, direction);
  };

  const prevClue = () => {
    const clues = direction === 'across' ? acrossClues : downClues;
    if (!activeClueNumber || clues.length === 0) return;
    
    const currentIndex = clues.findIndex(clue => clue.number === activeClueNumber);
    if (currentIndex === -1) return;
    
    const prevIndex = (currentIndex - 1 + clues.length) % clues.length;
    const prevClue = clues[prevIndex];
    
    onClueSelect(prevClue.row, prevClue.col, direction);
  };

  const toggleDirection = () => {
    if (activeCell) {
      const newDirection = direction === 'across' ? 'down' : 'across';
      const cell = gridData[activeCell.row][activeCell.col];
      
      if ((newDirection === 'across' && cell.isAcross) || (newDirection === 'down' && cell.isDown)) {
        setDirection(newDirection);
        
        // Update active clue number based on the new direction
        if (newDirection === 'across') {
          setActiveClue(cell.acrossClue);
        } else {
          setActiveClue(cell.downClue);
        }
      }
    }
  };

  // Handle letter input from keyboard
  const handleKeyPress = (letter: string) => {
    if (!activeCell || isComplete) return;
    
    const { row, col } = activeCell;
    if (letter === 'backspace') {
      // Create a simulated keyboard event for backspace
      const backspaceEvent = new KeyboardEvent('keydown', {
        key: 'Backspace',
        bubbles: true
      });
      gridRef.current?.handleKeyDown(backspaceEvent as unknown as React.KeyboardEvent, row, col);
    } else {
      // Create a simulated keyboard event for the letter
      const keyEvent = new KeyboardEvent('keydown', {
        key: letter.toUpperCase(),
        bubbles: true
      });
      gridRef.current?.handleKeyDown(keyEvent as unknown as React.KeyboardEvent, row, col);
    }
  };

  return (
    <div className="flex flex-col h-screen overflow-auto">
      {/* Top Navigation */}
      <header className="p-3 flex items-center justify-between shadow-md sticky top-0 z-10">
        <Button variant="ghost" size="icon" asChild className="h-9 w-9 text-primary hover:text-primary hover:bg-primary/10">
          <Link to="/">
            <ChevronLeft className="h-5 w-5" />
          </Link>
        </Button>
        
        <h1 className="text-primary font-bold text-2xl text-center flex-1">{title}</h1>
        
        <div className="w-9" /> {/* Spacer to maintain layout */}
      </header>

      {/* Main Grid Area */}
      <div className="flex-1 flex flex-col">
        <div className="flex flex-col justify-center items-center p-1">
          {/* Timer */}
          <div className="w-full max-w-md bg-primary rounded-lg py-2 shrink-0 mb-2">
            <Timer 
              isRunning={isTimerRunning}
              isComplete={isComplete}
              onReset={onReset}
              onTimerStop={onTimerStop}
              onCheck={onCheck}
              onReveal={onReveal}
              onShare={onShare}
              isMobile={true}
              interestTopic={title}
              shareUrl={shareUrl}
            />
          </div>

          {/* Crossword Grid */}
          {gridData.length > 0 && (
            <div style={{ 
              width: '100%',
              maxWidth: `${calculateGridSize() * Math.max(gridData.length, gridData[0].length)}px`,
              overflow: 'hidden'
            }}>
              <CrosswordGrid
                ref={gridRef}
                gridData={gridData}
                onCellChange={onCellChange}
                activeCell={activeCell}
                setActiveCell={setActiveCell}
                setActiveClue={setActiveClue}
                direction={direction}
                setDirection={setDirection}
                isComplete={isComplete}
              />
            </div>
          )}
        </div>
      </div>

      {/* Fixed bottom elements */}
      <div className="fixed bottom-0 left-0 right-0 z-20">
        {/* Current Clue Row */}
        <div className="bg-primary p-2 flex items-center text-white">
          <Button 
            variant="ghost" 
            size="icon" 
            className="shrink-0 text-white hover:text-white hover:bg-primary/80" 
            onClick={prevClue}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          
          <div className="flex-1 px-2" onClick={toggleDirection}>
            <div className="font-medium text-sm">
              {currentClue && (
                <>
                  <span className="font-semibold text-white">
                    {currentClue.number} {direction === 'across' ? 'Across' : 'Down'}:
                  </span>{' '}
                  {currentClue.clue}
                </>
              )}
            </div>
          </div>
          
          <Button 
            variant="ghost" 
            size="icon" 
            className="shrink-0 text-white hover:text-white hover:bg-primary/80" 
            onClick={nextClue}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Mobile Keyboard */}
        <div className="bg-gray-100">
          <MobileKeyboard onKeyPress={handleKeyPress} />
        </div>
      </div>

      {/* Add padding to account for fixed elements */}
      <div className="h-[120px]" />

      <CompletionDialog
        isOpen={showCompletionDialog}
        onClose={() => setShowCompletionDialog(false)}
        completionTime={completionTime}
        interestTopic={title}
        shareUrl={shareUrl}
        isMobile={true}
      />
    </div>
  );
};

export default MobileCrosswordLayout;
